import { useMemo, useState } from "react";
import { useParams } from "react-router";

import { Loader } from "components/elements";
import { PageError, SubTab, SubTabs } from "components/layout";
import { RestrictedArea, ROLES } from "containers/Permissions";
import { useVariantPublications, TiVoAppVariant } from "hooks/tivoApps";
import { usePlatforms } from "hooks/tivoDevices";

import {
  TiVoAppDetailsPublicationsEditSubTab,
  TiVoAppDetailsPublicationsSummarySubTab,
} from ".";
import { PublicationsLegend } from "../../PublicationsList";
import { TEXTS } from "../constants";
import styles from "../TiVoAppDetailsTabs.scss";

const EDIT_TAB = "edit";

const SUMMARY_TAB = "summary";

const DEFAULT_PUBLICATION = {
  id: "",
  oems: [],
  platforms: [],
  variant: {} as TiVoAppVariant,
};

export function TiVoAppDetailsPublicationsTab() {
  const [tabName, setTabName] = useState<string>(SUMMARY_TAB);
  const { id, variantId } = useParams();
  const {
    data: platformsData,
    isLoading: platformsLoading,
    error: platformsError,
  } = usePlatforms({
    include: ["oems", "oems.clientTypes"],
  });
  const {
    data: publicationData,
    isLoading: publicationLoading,
    error: publicationError,
  } = useVariantPublications(id, variantId, {
    include: ["oems", "oems.clientTypes", "variant", "variant.versions"],
  });

  const subTabProps = useMemo(
    () => ({
      platforms: platformsData?.results ?? [],
      publication: publicationData?.results || DEFAULT_PUBLICATION,
    }),
    [platformsData, publicationData]
  );

  if (platformsLoading || publicationLoading) {
    return <Loader />;
  }

  if (platformsError || publicationError) {
    return (
      <PageError
        pageHeader={TEXTS.PUBLICATIONS.ERROR_HEADER}
        error={platformsError || publicationError}
      />
    );
  }

  return (
    <>
      <SubTabs className={styles.tabs}>
        <SubTab
          active={tabName === SUMMARY_TAB}
          text={TEXTS.PUBLICATIONS.SUMMARY_TAB}
          onClick={() => setTabName(SUMMARY_TAB)}
        />
        <RestrictedArea allowed={[ROLES.tivo.moderator]} showFallback={false}>
          <SubTab
            active={tabName === EDIT_TAB}
            text={TEXTS.PUBLICATIONS.EDIT_TAB}
            onClick={() => setTabName(EDIT_TAB)}
          />
        </RestrictedArea>
      </SubTabs>
      {tabName === EDIT_TAB ? (
        <TiVoAppDetailsPublicationsEditSubTab {...subTabProps} />
      ) : (
        <TiVoAppDetailsPublicationsSummarySubTab {...subTabProps} />
      )}
      <PublicationsLegend className={styles.legend} />
    </>
  );
}
